import { useState, useEffect, useCallback } from 'react'
import { useDrawer, DrawerFooter } from '@/components/ui/drawer'
import { DrawerSkeleton } from '@/components/ui/drawer/DrawerSkeleton'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Checkbox } from '@/components/ui/checkbox'
import { useDrawerForm } from '@/hooks/useDrawerForm'
import { useDrawerKeyboard } from '@/hooks/useDrawerKeyboard'
import { useDrawerMessaging } from '@/hooks/useDrawerMessaging'
import { 
  usersService, 
  type User, 
  type CreateUserDto, 
  type UpdateUserDto,
  // getUserRoleLabel,
  // getUserStatusLabel
} from '@/services/users'
import { tenantsService, type Tenant } from '@/services/tenants'
import { toast } from 'sonner'
import { User as UserIcon, Mail, Phone, Shield, Calendar, Package, Key, CheckCircle, AlertCircle } from 'lucide-react'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'

interface UserFormDrawerProps {
  userId?: string
  onSuccess?: (user: User) => void
}

// Store form state globally for access by footer
const formStates = new Map<string, {
  handleSave: () => Promise<void>
  isDirty: boolean
  saving: boolean
}>()

// Available permissions
const AVAILABLE_PERMISSIONS = [
  { id: 'users.view', label: 'View Users', category: 'Users' },
  { id: 'users.create', label: 'Create Users', category: 'Users' },
  { id: 'users.edit', label: 'Edit Users', category: 'Users' },
  { id: 'users.delete', label: 'Delete Users', category: 'Users' },
  { id: 'tenants.view', label: 'View Tenants', category: 'Tenants' },
  { id: 'tenants.create', label: 'Create Tenants', category: 'Tenants' },
  { id: 'tenants.edit', label: 'Edit Tenants', category: 'Tenants' },
  { id: 'tenants.delete', label: 'Delete Tenants', category: 'Tenants' },
  { id: 'billing.view', label: 'View Billing', category: 'Billing' },
  { id: 'billing.manage', label: 'Manage Billing', category: 'Billing' },
  { id: 'analytics.view', label: 'View Analytics', category: 'Analytics' },
  { id: 'support.view', label: 'View Support', category: 'Support' },
  { id: 'support.manage', label: 'Manage Support', category: 'Support' },
  { id: 'impersonate', label: 'Impersonate Users', category: 'Security' },
]

export function UserFormDrawer({ userId, onSuccess }: UserFormDrawerProps) {
  const { closeDrawer, updateDrawer } = useDrawer()
  const { broadcast } = useDrawerMessaging(`user-form-${userId || 'new'}`)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [user, setUser] = useState<User | null>(null)
  const [dataLoaded, setDataLoaded] = useState(false)
  const [tenants, setTenants] = useState<Tenant[]>([])
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([])
  
  const isEditMode = !!userId
  const drawerId = `user-form-${userId || 'new'}`
  
  const [formData, setFormData] = useState<CreateUserDto>({
    email: '',
    first_name: '',
    last_name: '',
    password: '',
    role: 'user',
    phone: '',
    tenant_id: '',
    permissions: [],
  })

  const [status, setStatus] = useState<User['status']>('active')

  // Use drawer form hook for auto-save and dirty tracking
  const {
    isDirty,
    markAsSaved,
    setInitialData,
  } = useDrawerForm(formData, setFormData, {
    drawerId,
    autoSave: false, // Disable auto-save for user data for security
    confirmClose: true,
  })

  // Load user data if editing - only once. Lives below useDrawerForm so the
  // loaded record can be set as the pristine snapshot for dirty tracking.
  useEffect(() => {
    const loadData = async () => {
      setLoading(true)

      try {
        // Load tenants
        const tenantsData = await tenantsService.list()
        setTenants(tenantsData)

        // Load user if editing
        if (isEditMode && userId && !dataLoaded) {
          const userData = await usersService.get(userId)
          setUser(userData)
          // Split full_name back into first_name and last_name for editing
          const nameParts = userData.full_name?.split(' ') || ['', '']
          const first_name = nameParts[0] || ''
          const last_name = nameParts.slice(1).join(' ') || ''

          const loadedForm = {
            email: userData.email,
            first_name,
            last_name,
            password: '', // Don't show password
            role: userData.role,
            phone: userData.phone || '',
            tenant_id: userData.tenant_id || '',
            permissions: userData.permissions || [],
          }
          setFormData(loadedForm)
          setInitialData(loadedForm)
          setStatus(userData.status)
          setSelectedPermissions(userData.permissions || [])
          setDataLoaded(true)
        }
      } catch (error) {
        console.error('Failed to load data:', error)
        toast.error('Failed to load data')
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [isEditMode, userId, dataLoaded, setInitialData])

  const validateForm = useCallback((): boolean => {
    const errors: string[] = []

    if (!formData.email.trim()) {
      errors.push('Email is required')
    }
    if (!formData.first_name.trim()) {
      errors.push('First name is required')
    }
    if (!isEditMode && !formData.password.trim()) {
      errors.push('Password is required')
    }
    if (!isEditMode && formData.password.length < 8) {
      errors.push('Password must be at least 8 characters')
    }
    if (formData.email && !isValidEmail(formData.email)) {
      errors.push('Invalid email format')
    }

    if (errors.length > 0) {
      toast.error(errors[0])
      return false
    }

    return true
  }, [formData, isEditMode])

  function isValidEmail(email: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
  }

  const handleSave = useCallback(async () => {
    if (!validateForm()) return
    
    setSaving(true)
    
    try {
      let savedUser: User
      
      if (isEditMode && userId) {
        const updateData: UpdateUserDto = {
          first_name: formData.first_name,
          last_name: formData.last_name,
          role: formData.role,
          phone: formData.phone,
          tenant_id: formData.tenant_id,
          status,
          permissions: selectedPermissions,
        }
        savedUser = await usersService.update(userId, updateData)
        toast.success('User updated successfully')
      } else {
        const createData: CreateUserDto = {
          ...formData,
          permissions: selectedPermissions,
        }
        savedUser = await usersService.create(createData)
        toast.success('User created successfully')
      }
      
      markAsSaved()
      
      // Send refresh message to other drawers
      broadcast('refresh', { userId: savedUser.id })
      
      // Call success callback
      onSuccess?.(savedUser)
      
      // Show success animation and close
      updateDrawer(drawerId, { loadingState: 'success' })
      setTimeout(() => {
        closeDrawer(drawerId)
      }, 1000)
    } catch (error) {
      console.error('Failed to save user:', error)
      toast.error(isEditMode ? 'Failed to update user' : 'Failed to create user')
    } finally {
      setSaving(false)
    }
  }, [formData, status, selectedPermissions, isEditMode, userId, markAsSaved, broadcast, onSuccess, updateDrawer, drawerId, closeDrawer, validateForm])

  // Keyboard shortcuts
  useDrawerKeyboard(drawerId, {
    onSave: handleSave,
    onSubmit: handleSave,
  })

  // Store form state for footer access
  useEffect(() => {
    formStates.set(drawerId, {
      handleSave,
      isDirty,
      saving,
    })
    
    return () => {
      formStates.delete(drawerId)
    }
  }, [drawerId, handleSave, isDirty, saving])

  const handleInputChange = (field: keyof CreateUserDto, value: string | boolean | string[]) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const togglePermission = (permissionId: string) => {
    setSelectedPermissions(prev => 
      prev.includes(permissionId)
        ? prev.filter(p => p !== permissionId)
        : [...prev, permissionId]
    )
  }

  if (loading) {
    return <DrawerSkeleton sections={4} />
  }

  return (
    <div className="space-y-6">
      {/* User Information */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <UserIcon className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-semibold">User Information</h3>
        </div>
        
        <div className="space-y-4">
          {isEditMode && user && (
            <div className="flex items-center gap-4 p-4 rounded-lg bg-muted/50">
              <Avatar className="h-16 w-16">
                <AvatarImage src={user.avatar_url} alt={user.full_name} />
                <AvatarFallback>
                  {user.full_name.split(' ').map(n => n[0]).join('').toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div>
                <div className="font-semibold text-lg">{user.full_name}</div>
                <div className="text-sm text-muted-foreground">{user.email}</div>
                <div className="flex items-center gap-2 mt-1">
                  {user.email_verified ? (
                    <Badge variant="outline" className="text-green-600">
                      <CheckCircle className="mr-1 h-3 w-3" />
                      Verified
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-yellow-600">
                      <AlertCircle className="mr-1 h-3 w-3" />
                      Unverified
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          )}
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="first_name">First Name *</Label>
              <Input
                id="first_name"
                value={formData.first_name}
                onChange={(e) => handleInputChange('first_name', e.target.value)}
                placeholder="Enter first name"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="last_name">Last Name</Label>
              <Input
                id="last_name"
                value={formData.last_name}
                onChange={(e) => handleInputChange('last_name', e.target.value)}
                placeholder="Enter last name"
                className="mt-1"
              />
            </div>
          </div>
          
          <div>
            <Label htmlFor="email">Email Address *</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                placeholder="email@example.com"
                className="mt-1 pl-10"
                disabled={isEditMode}
              />
            </div>
          </div>
          
          {!isEditMode && (
            <div>
              <Label htmlFor="password">Password *</Label>
              <div className="relative">
                <Key className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  placeholder="Enter password (min 8 characters)"
                  className="mt-1 pl-10"
                />
              </div>
            </div>
          )}
          
          <div>
            <Label htmlFor="phone">Phone Number</Label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                placeholder="+1 (555) 000-0000"
                className="mt-1 pl-10"
              />
            </div>
          </div>
        </div>
      </div>

      <Separator />

      {/* Role & Access */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <Shield className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-semibold">Role & Access</h3>
        </div>
        
        <div className="space-y-4">
          <div className="flex gap-4">
            <div className="flex-1">
              <Label htmlFor="role">Role *</Label>
              <Select 
                value={formData.role} 
                onValueChange={(value: User['role']) => handleInputChange('role', value)}
              >
                <SelectTrigger id="role" className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="super_admin">Super Admin</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="sales_rep">Sales Rep</SelectItem>
                  <SelectItem value="site_surveyor">Site Surveyor</SelectItem>
                  <SelectItem value="user">User</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {isEditMode && (
              <div className="flex-1">
                <Label htmlFor="status">Status</Label>
                <Select value={status} onValueChange={(value: User['status']) => setStatus(value)}>
                  <SelectTrigger id="status" className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                    <SelectItem value="suspended">Suspended</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          
          <div>
            <Label htmlFor="tenant_id">Tenant Organization</Label>
            <Select 
              value={formData.tenant_id} 
              onValueChange={(value) => handleInputChange('tenant_id', value === 'none' ? '' : value)}
            >
              <SelectTrigger id="tenant_id" className="mt-1">
                <SelectValue placeholder="Select a tenant..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No Tenant (Platform Admin)</SelectItem>
                {tenants.map((tenant) => (
                  <SelectItem key={tenant.tenant_id} value={tenant.tenant_id}>
                    {tenant.company_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <Separator />

      {/* Permissions */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <Shield className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-semibold">Custom Permissions</h3>
        </div>
        
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Grant specific permissions beyond the default role permissions.
          </p>
          
          {/* Group permissions by category */}
          {Object.entries(
            AVAILABLE_PERMISSIONS.reduce((acc, perm) => {
              if (!acc[perm.category]) acc[perm.category] = []
              acc[perm.category].push(perm)
              return acc
            }, {} as Record<string, typeof AVAILABLE_PERMISSIONS>)
          ).map(([category, perms]) => (
            <div key={category}>
              <h4 className="font-medium mb-2">{category}</h4>
              <div className="space-y-2">
                {perms.map((perm) => (
                  <div key={perm.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={perm.id}
                      checked={selectedPermissions.includes(perm.id)}
                      onCheckedChange={() => togglePermission(perm.id)}
                    />
                    <Label
                      htmlFor={perm.id}
                      className="text-sm font-normal cursor-pointer"
                    >
                      {perm.label}
                    </Label>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Metadata for edit mode */}
      {isEditMode && user && (
        <>
          <Separator />
          <div>
            <h3 className="text-lg font-semibold mb-4">Metadata</h3>
            <div className="space-y-2 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                <span>Created: {new Date(user.created_at).toLocaleDateString()}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                <span>Updated: {new Date(user.updated_at).toLocaleDateString()}</span>
              </div>
              {user.last_login && (
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  <span>Last Login: {new Date(user.last_login).toLocaleDateString()}</span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <Package className="h-4 w-4" />
                <span>ID: {user.id}</span>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Status indicator */}
      {isDirty && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
          <p className="text-sm text-yellow-800">
            You have unsaved changes. Press <kbd className="px-1 py-0.5 text-xs bg-yellow-100 rounded">{navigator.platform.toUpperCase().includes('MAC') ? 'Cmd' : 'Ctrl'}+S</kbd> to save.
          </p>
        </div>
      )}
    </div>
  )
}

// Footer component that accesses form state
export function UserFormFooter({ isEditMode, drawerId }: { 
  isEditMode: boolean
  drawerId: string
  userId?: string 
}) {
  const { closeDrawer } = useDrawer()
  const [formState, setFormState] = useState(formStates.get(drawerId))
  
  useEffect(() => {
    const interval = setInterval(() => {
      setFormState(formStates.get(drawerId))
    }, 100)
    
    return () => clearInterval(interval)
  }, [drawerId])

  return (
    <DrawerFooter>
      <Button
        variant="outline"
        onClick={() => closeDrawer(drawerId)}
        disabled={formState?.saving}
      >
        Cancel
      </Button>
      <Button
        onClick={() => formState?.handleSave()}
        disabled={formState?.saving || (!isEditMode && !formState?.isDirty)}
      >
        {formState?.saving ? 'Saving...' : isEditMode ? 'Update User' : 'Create User'}
      </Button>
    </DrawerFooter>
  )
}